import React, { useState, useEffect, useCallback } from 'react';
import LearnFolderItem from './LearnFolderItem';
import LearnBookmarkItem from './LearnBookmarkItem';
import { learnApi } from '../../api/learn';
import ConfirmModal from '../Common/ConfirmModal';
import { useConfirm } from '../Common/useConfirm';

/**
 * LearnLibraryTab - Library interface for managing folders and bookmarks.
 *
 * Features:
 * - Folder list with expand/collapse
 * - Uncategorized bookmarks section
 * - Create/rename/delete folders
 * - Move bookmarks between folders
 */
export default function LearnLibraryTab({ onBookmarkClick }) {
  const [library, setLibrary] = useState({ folders: [], uncategorized: [] });
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const { confirm, modalProps } = useConfirm();

  // Load library on mount
  useEffect(() => {
    loadLibrary();
  }, []);

  const loadLibrary = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await learnApi.getLibrary();
      if (response?.data) {
        setLibrary(response.data);
      }
    } catch (err) {
      console.error('Failed to load library:', err);
      setError('Failed to load library.');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleFolder = useCallback((folderId) => {
    setExpandedFolders(prev => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  }, []);

  const handleCreateFolder = async () => {
    const name = newFolderName.trim();
    if (!name) return;

    try {
      const response = await learnApi.createFolder(name);
      if (response?.data) {
        setLibrary(prev => ({
          ...prev,
          folders: [...prev.folders, { ...response.data, bookmarks: [] }]
        }));
        setNewFolderName('');
        setIsCreatingFolder(false);
      }
    } catch (err) {
      console.error('Failed to create folder:', err);
      setError('Failed to create folder.');
    }
  };

  const handleRenameFolder = async (folderId, newName) => {
    try {
      const response = await learnApi.updateFolder(folderId, { name: newName });
      if (response?.data) {
        setLibrary(prev => ({
          ...prev,
          folders: prev.folders.map(f =>
            f.id === folderId ? { ...f, name: response.data.name } : f
          )
        }));
      }
    } catch (err) {
      console.error('Failed to rename folder:', err);
    }
  };

  const handleDeleteFolder = async (folderId) => {
    const ok_f = await confirm({ title: 'Delete folder?', message: 'Delete this folder? Bookmarks will move to Uncategorized.', confirmLabel: 'Delete', destructive: true });
    if (!ok_f) return;

    try {
      await learnApi.deleteFolder(folderId);

      // Move bookmarks from deleted folder to uncategorized
      const deletedFolder = library.folders.find(f => f.id === folderId);
      const bookmarksToMove = deletedFolder?.bookmarks || [];

      setLibrary(prev => ({
        folders: prev.folders.filter(f => f.id !== folderId),
        uncategorized: [...bookmarksToMove, ...prev.uncategorized]
      }));
    } catch (err) {
      console.error('Failed to delete folder:', err);
    }
  };

  const handleMoveBookmark = async (bookmarkId, newFolderId) => {
    try {
      await learnApi.updateBookmark(bookmarkId, newFolderId);
      // Reload library to get updated structure
      await loadLibrary();
    } catch (err) {
      console.error('Failed to move bookmark:', err);
    }
  };

  const handleDeleteBookmark = async (bookmarkId) => {
    const ok_b = await confirm({ title: 'Delete bookmark?', message: 'Delete this bookmark?', confirmLabel: 'Delete', destructive: true });
    if (!ok_b) return;

    try {
      await learnApi.deleteBookmark(bookmarkId);

      // Remove from local state
      setLibrary(prev => ({
        folders: prev.folders.map(f => ({
          ...f,
          bookmarks: f.bookmarks.filter(b => b.id !== bookmarkId)
        })),
        uncategorized: prev.uncategorized.filter(b => b.id !== bookmarkId)
      }));
    } catch (err) {
      console.error('Failed to delete bookmark:', err);
    }
  };

  if (isLoading) {
    return (
      <div className="learn-library-tab learn-library-tab--loading">
        <div className="learn-panel__spinner" />
        <span>Loading library...</span>
      </div>
    );
  }

  const isEmpty = library.folders.length === 0 && library.uncategorized.length === 0;

  return (
    <div className="learn-library-tab">
      {/* Header */}
      <div className="learn-library-header">
        <h3>Library</h3>
        <button
          className="learn-library-add-btn"
          onClick={() => setIsCreatingFolder(true)}
          title="Create folder"
        >
          + Folder
        </button>
      </div>

      {error && (
        <div className="learn-library-error">{error}</div>
      )}

      {/* Create folder input */}
      {isCreatingFolder && (
        <div className="learn-library-create-folder">
          <input
            type="text"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreateFolder();
              if (e.key === 'Escape') setIsCreatingFolder(false);
            }}
            placeholder="Folder name..."
            autoFocus
          />
          <button onClick={handleCreateFolder}>Create</button>
          <button onClick={() => setIsCreatingFolder(false)}>Cancel</button>
        </div>
      )}

      {isEmpty ? (
        <div className="learn-library-empty">
          <p>No bookmarks yet.</p>
          <p className="learn-library-empty__hint">
            Bookmark helpful responses in the Chat tab to save them here.
          </p>
        </div>
      ) : (
        <>
          {/* Folders */}
          <div className="learn-library-folders">
            {library.folders.map(folder => (
              <LearnFolderItem
                key={folder.id}
                folder={folder}
                isExpanded={expandedFolders.has(folder.id)}
                onToggle={() => toggleFolder(folder.id)}
                onRename={(newName) => handleRenameFolder(folder.id, newName)}
                onDelete={() => handleDeleteFolder(folder.id)}
                onBookmarkClick={onBookmarkClick}
                onMoveBookmark={handleMoveBookmark}
                onDeleteBookmark={handleDeleteBookmark}
                allFolders={library.folders}
              />
            ))}
          </div>

          {/* Uncategorized */}
          {library.uncategorized.length > 0 && (
            <div className="learn-library-uncategorized">
              <h4>Uncategorized</h4>
              {library.uncategorized.map(bookmark => (
                <LearnBookmarkItem
                  key={bookmark.id}
                  bookmark={bookmark}
                  onClick={() => onBookmarkClick?.(bookmark)}
                  onMove={(folderId) => handleMoveBookmark(bookmark.id, folderId)}
                  onDelete={() => handleDeleteBookmark(bookmark.id)}
                  allFolders={library.folders}
                />
              ))}
            </div>
          )}
        </>
      )}
      <ConfirmModal {...modalProps} />
    </div>
  );
}
